/**
 * Cursor arithmetic for the audit log.
 *
 * The API offers no cursor and no offset: only `since` (inclusive), `until` (exclusive)
 * and `limit`. Timestamps are rendered to microseconds and round-trip exactly, so paging
 * backwards means moving `until` to the oldest timestamp already seen.
 *
 * Two traps:
 *
 * 1. `until = oldest.ts` would use an EXCLUSIVE bound equal to a timestamp we have seen,
 *    which drops every other row sharing that microsecond. The bound is therefore
 *    `oldest.ts + 1µs`: the oldest microsecond is re-read whole and the duplicates are
 *    removed by id. Re-reading one microsecond is cheap; losing a row is not.
 *
 * 2. `Date` is millisecond-precision and would silently truncate the last three digits.
 *    Everything here is BigInt epoch-microseconds.
 */

const TS_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?Z$/;

export function tsToMicros(ts: string): bigint {
  const m = TS_RE.exec(ts);
  if (!m) throw new RangeError(`audit ts is not the API's RFC 3339 UTC form: ${ts}`);
  const [, y, mo, d, h, mi, s, frac] = m as unknown as [string, string, string, string, string, string, string, string | undefined];
  const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
  return BigInt(ms) * 1000n + BigInt((frac ?? '').padEnd(6, '0') || '0');
}

export function microsToTs(us: bigint): string {
  const seconds = us / 1_000_000n;
  const frac = us % 1_000_000n;
  const base = new Date(Number(seconds) * 1000).toISOString();
  return base.replace(/\.\d{3}Z$/, `.${frac.toString().padStart(6, '0')}Z`);
}

/** The next page's exclusive upper bound. */
export const nextUntilFrom = (oldestTs: string): string => microsToTs(tsToMicros(oldestTs) + 1n);

export const sameMicrosecond = (a: string, b: string): boolean => tsToMicros(a) === tsToMicros(b);

/** True when every row shares one microsecond, so `until` cannot advance past them. */
export function allOneMicrosecond(rows: readonly { ts: string }[]): boolean {
  if (rows.length < 2) return false;
  const first = tsToMicros(rows[0]!.ts);
  return rows.every((r) => tsToMicros(r.ts) === first);
}

export const AUDIT_DEFAULT_LIMIT = 100;
export const AUDIT_MAX_LIMIT = 1000;
